import { useEffect, useId, useRef, useState } from 'react';
import { useDialogA11y } from '../../hooks/useDialogA11y';

export type InputModalProps = {
  open: boolean;
  title?: string;
  label?: string;
  placeholder?: string;
  initialValue?: string;
  okText?: string;
  cancelText?: string;
  textarea?: boolean;
  onSubmit: (value: string) => void;
  onCancel: () => void;
};

export default function InputModal({ open, title = 'Input', label, placeholder, initialValue = '', okText = 'Save', cancelText = 'Cancel', textarea = false, onSubmit, onCancel }: InputModalProps) {
  const [val, setVal] = useState(initialValue);
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(open, dialogRef, onCancel);

  useEffect(() => {
    if (open) {
      setVal(initialValue || '');
      setTimeout(() => ref.current?.focus(), 10);
    }
  }, [open, initialValue]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={title ? titleId : undefined} tabIndex={-1} className="relative w-full max-w-md rounded-2xl border border-neutral-800 bg-neutral-900 p-4 shadow-2xl outline-none">
        {title && <div id={titleId} className="text-brand font-medium mb-2">{title}</div>}
        {label && <label className="block text-xs text-fg-3 mb-1">{label}</label>}
        {textarea ? (
          <textarea ref={ref as any} rows={4} className="w-full p-2.5 rounded-xl bg-neutral-950 text-fg placeholder-neutral-500 border border-neutral-800 focus:outline-none focus:ring-2 focus:ring-brand/60" placeholder={placeholder} aria-label={label || title || placeholder} value={val} onChange={(e)=>setVal(e.target.value)} spellCheck={true} autoCorrect="on" autoCapitalize="sentences" />
        ) : (
          <form onSubmit={(e)=>{ e.preventDefault(); onSubmit(val.trim()); }}>
            <input ref={ref as any} className="w-full p-2.5 rounded-xl bg-neutral-950 text-fg placeholder-neutral-500 border border-neutral-800 focus:outline-none focus:ring-2 focus:ring-brand/60" placeholder={placeholder} aria-label={label || title || placeholder} enterKeyHint="done" value={val} onChange={(e)=>setVal(e.target.value)} />
            <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
          </form>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button className="px-3 py-2 rounded-xl border border-neutral-700 text-fg hover:bg-neutral-800/70" onClick={onCancel}>{cancelText}</button>
          <button className="px-3 py-2 rounded-xl border border-brand/60 bg-brand/20 text-brand hover:bg-brand/30" onClick={()=>onSubmit(val.trim())}>{okText}</button>
        </div>
      </div>
    </div>
  );
}
